import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import RZLib from 'src/app/core/backend-adapter/db2/RZ-lib.helpers';
import { ProgressComponent } from 'src/app/core/feature-modules/progress/progress.component';
import { ABPService } from 'src/app/abp-management/abp.service';
import { ABPPhotoConfigService } from 'src/app/abp-management/services/abp-photo-config-service/abp-photo-config-service';
import { SuccessComponent } from '../success/success.component';
import { SocketService } from 'src/app/core/backend-adapter/socket.service';

export interface IOfferSaveObject {
  account_id: number;
  summary: string;
  languageCode: string;
  topicType: string;
  sourceURL: string;
  location_ids: any[] | string;
  scheduleTime: any;
  eventStartDate: any;
  eventEndDate: any;
  eventTitle: string;
  scheduleTimeZone: number;
  // ctaActionType: string;
  // ctaURL: string;
  offerCouponCode?: string;
  offerRedeemURL?: string;
  offerTerms?: string;
  eventStartTime?: any;
  eventEndTime?: any;
  _status: string;
  _id?: number;
}
@Component({
  selector: 'app-offer-post',
  templateUrl: './offer-post.component.html',
  styleUrls: ['./offer-post.component.scss'],
})
export class OfferPostComponent implements OnInit {
  @Input() rowData;

  stepsLabels = ['Add Photo', 'Post Details', 'Offer Info', 'Schedule and Select Location', 'Review and Submit'];
  activeIndex = 0;
  offerForm = new FormGroup({
    upload: new FormGroup({
      imageUrl: new FormControl(''),
    }),
    postDetails: new FormGroup({
      postDescription: new FormControl('', Validators.required),
      isAddTime: new FormControl(false),
      startDate: new FormControl('', Validators.required),
      endDate: new FormControl('', Validators.required),
      startTime: new FormControl(''),
      endTime: new FormControl(''),
      languageCode: new FormControl('', Validators.required),
    }),
    offerInfo: new FormGroup({
      offerTitle: new FormControl('', Validators.required),
      couponCode: new FormControl(''),
      linkToRedeemOffer: new FormControl(
        '',
        Validators.pattern(
          /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#%[\]{}@!\$&'\(\)\*\+,;=]*(\{\{[A-Za-z0-9.]+\}\}|[\w\-\._~:/?#[\]{}@!\$&'\(\)\*\+,;=])*\/?$/
        )
      ),
      termsAndConditions: new FormControl(''),
      addButton: new FormControl(''),
    }),
    schedule: new FormGroup({
      checked: new FormControl(false),
      date: new FormControl(''),
      time: new FormControl(''),
      timeZone: new FormControl(''),
      location: new FormControl([]),
      isSelectedAll: new FormControl(''),
      ctaFiltersQueryParams: new FormControl({}),
      ctaSummaryFromFilterApply: new FormControl(null),
    }),
  });
  uploadGroup: FormGroup;
  postDetailsGroup: FormGroup;
  scheduleGroup: FormGroup;
  offerInfoGroup: FormGroup;
  formGroupName = 'upload';
  saveObj;
  closeAbpUtmFormEvent = new EventEmitter<void>();
  preSelectedLocation: any[] = [];

  /**
   * @description To display validations rules to upload an Image.
   */
  public photoRequirements: string[] = [];
  /**
   * @description Validation rules Title.
   */
  public photoRequirementsTitle: string;

  constructor(
    public dynamicDialogConfig: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    public dialogService: DialogService,
    private abpService: ABPService,
    private abpPhotoConfigService: ABPPhotoConfigService,
    private socketService: SocketService,
  ) {}

  ngOnInit(): void {
    this.uploadGroup = this.offerForm.controls.upload as FormGroup;
    this.postDetailsGroup = this.offerForm.controls.postDetails as FormGroup;
    this.offerInfoGroup = this.offerForm.controls.offerInfo as FormGroup;
    this.scheduleGroup = this.offerForm.controls.schedule as FormGroup;
    this.setPhotoValidationRules();

    if (this.rowData) {
      this.setInitialFormValues();
    }
  }

  /**
   * @description Method set's validation rules on Add Photo screen for Media type PHOTO.
   * @returns void
   */
  private setPhotoValidationRules(): void {
    this.photoRequirements = this.abpPhotoConfigService.setABPMediaRequirements('PHOTO').description;
    this.photoRequirementsTitle = this.abpPhotoConfigService.setABPMediaRequirements('PHOTO').title;
  }

  private setInitialFormValues() {
    this.uploadGroup.setValue({
      imageUrl: this.rowData.internalURL || this.rowData?.sourceURL,
    });

    const today = new Date();
    this.postDetailsGroup.patchValue({
      postDescription: this.rowData?.summary,
      isAddTime: this.rowData?.eventStartTime ? true : false,
      startDate: new Date(this.rowData?.eventStartDate._year_, this.rowData?.eventStartDate._month_ - 1, this.rowData?.eventStartDate._day_),
      endDate: new Date(this.rowData?.eventEndDate._year_, this.rowData?.eventEndDate._month_ - 1, this.rowData?.eventEndDate._day_),
      startTime: this.rowData?.eventStartTime
        ? new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            this.rowData.eventStartTime.hour,
            this.rowData.eventStartTime.minute,
            0
          )
        : new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0),
      endTime: this.rowData?.eventEndTime
        ? new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            this.rowData.eventEndTime.hour,
            this.rowData.eventEndTime.minute,
            0
          )
        : new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59),
      languageCode: this.abpService.preparePostLanguage(this.rowData?.languageCode),
    });

    this.offerInfoGroup.patchValue({
      offerTitle: this.rowData?.eventTitle,
      couponCode: this.rowData?.offerCouponCode,
      linkToRedeemOffer: this.rowData?.offerRedeemURL,
      termsAndConditions: this.rowData?.offerTerms,
      addButton: this.rowData?.ctaActionType && this.rowData.ctaActionType?.replace(' ', '_') || '',
    });

    // Set schedule Date, Time and Timezone.
    const scheduledTime = this.rowData.scheduleTime !== null ?
      (new Date(new Date().getDate(), new Date().getMonth() + 1, new Date().getFullYear(), this.rowData.scheduleTime?._hour_, this.rowData.scheduleTime?._minute_, this.rowData.scheduleTime?._second_)) : '';
    const scheduledDate = this.rowData.scheduleTime !== null ?
      (new Date(`${this.rowData.scheduleTime?._month_}/${this.rowData.scheduleTime?._day_}/${this.rowData.scheduleTime?._year_}`)) : '';
    this.scheduleGroup.patchValue({
      date: scheduledDate,
      time: scheduledTime,
      timeZone: this.rowData?.scheduleTimeZone,
    });

    // Add a new control if Post status === A (Active).
    if (this.rowData._status === 'A') {
      this.scheduleGroup.addControl('postStatus', new FormControl(true));
    }

    this.socketService.sendRequest('get-localpost-detail-coll', { 'detailLevel': 3, 'where': ['-and', `post_id=${this.rowData._id}`] })
    .then(res => {
      res['collection'].forEach(l => {
        this.preSelectedLocation.push({
          _id: l.location_id,
          storeCode: l.storeCode,
          businessName: l.businessName,
          city: l.city,
          state: l.state,
          disabled: true
        });
      });

      // Added a new control to store pre-selected locations.
      this.scheduleGroup.addControl('preSelectedLocation', new FormControl(
        [...this.preSelectedLocation]
      ));
    })

  }

  private prepareOfferSaveObject(postDetailsGroup, offerInfoGroup, scheduleGroup, uploadGroup) {
    const tempObj: IOfferSaveObject = {
      account_id: 0,
      summary: '',
      languageCode: '',
      topicType: '',
      sourceURL: '',
      location_ids: [],
      scheduleTime: new RZLib.Timestamp().setNow(),
      eventStartDate: new RZLib.Timestamp().setNow(),
      eventEndDate: new RZLib.Timestamp().setNow(),
      eventTitle: '',
      scheduleTimeZone: 0,
      _status: 'AP',
    };

    tempObj.account_id = this.abpService.account_id;
    tempObj.summary = postDetailsGroup.postDescription;
    tempObj.languageCode = postDetailsGroup.languageCode;
    tempObj.topicType = 'OFFER';
    const urlFieldName = new RegExp(/^https:\/\/rseo-platform(-dev)?.s3.amazonaws.com\/media\/.+/i).test(
      uploadGroup.imageUrl
    )
      ? 'internalURL'
      : 'sourceURL';
    tempObj[urlFieldName] = uploadGroup.imageUrl;

    tempObj.eventStartDate = this.abpService.prepareDate(postDetailsGroup.startDate, tempObj.eventStartDate);
    tempObj.eventEndDate = this.abpService.prepareDate(postDetailsGroup.endDate, tempObj.eventEndDate);

    tempObj.eventTitle = offerInfoGroup.offerTitle;
    // tempObj.ctaActionType = offerInfoGroup.addButton.toUpperCase();
    // tempObj.ctaURL = postDetailsGroup.ctaURL || '';
    tempObj.offerCouponCode = offerInfoGroup.couponCode;
    tempObj.offerRedeemURL = offerInfoGroup.linkToRedeemOffer;
    tempObj.offerTerms = offerInfoGroup.termsAndConditions;

    tempObj.location_ids = scheduleGroup.isSelectedAll
      ? '*'
      : scheduleGroup.location?.length > 0
      ? this.abpService.mapAllLocations(scheduleGroup.location, this.preSelectedLocation)
      : null;
    tempObj.scheduleTime = scheduleGroup.checked ? this.abpService.prepareDate(scheduleGroup.date, tempObj.scheduleTime, scheduleGroup.time) : '';
    tempObj.scheduleTimeZone = scheduleGroup.checked ? scheduleGroup.timeZone : null;

    if (postDetailsGroup.startTime && postDetailsGroup.endTime) {
      tempObj.eventStartTime = new RZLib.Timestamp().setNow();
      tempObj.eventEndTime = new RZLib.Timestamp().setNow();
      tempObj.eventStartTime = this.abpService.prepareTime(tempObj.eventStartTime, postDetailsGroup.startTime);
      tempObj.eventEndTime = this.abpService.prepareTime(tempObj.eventEndTime, postDetailsGroup.endTime);
    }

    // Edit mode
    if (this.rowData) {
      tempObj._id = this.rowData._id;
    }

    return tempObj;
  }

  public goNext(progress: ProgressComponent): void {
    if (this.closeAbpUtmFormEvent) this.closeAbpUtmFormEvent.emit();
    progress.next();
  }

  onStateChange(event) {
    if (event.status == 'completed') {
      // send data to backend

      this.saveObj = this.prepareOfferSaveObject(
        this.postDetailsGroup.value,
        this.offerInfoGroup.value,
        this.scheduleGroup.value,
        this.uploadGroup.value
      );

      // console.log(this.saveObj);
      this.abpService.updateLocalPost(this.saveObj);
      this.ref.close();
      this.dialogService.open(SuccessComponent, {
        width: '20%',
      });
    }
    this.activeIndex = event.activeIndex;
    switch (this.activeIndex) {
      case 0: {
        this.formGroupName = 'upload';
        break;
      }
      case 1: {
        this.formGroupName = 'postDetails';
        break;
      }
      case 2: {
        this.formGroupName = 'offerInfo';
        break;
      }
      case 3: {
        this.formGroupName = 'schedule';
        break;
      }
    }
  }

  closeDialog() {
    this.ref.close();
  }

  imageChanged(url) {
    this.offerForm.patchValue({ upload: { imageUrl: url[0] } });
  }

  public get checkDateAndLocStatus(): boolean {
    const isChecked = this.scheduleGroup.controls;
    let dateSelection: boolean;
    if (this.formGroupName === 'schedule') {
      if (!isChecked['checked'].value) {
        return (dateSelection =
          !isChecked['checked'].value && (isChecked['location'].value.length || isChecked['isSelectedAll'].value || isChecked['preSelectedLocation']?.value?.length)
            ? false
            : true);
      } else {
        return (dateSelection =
          isChecked['date'].value &&
          isChecked['time'].value &&
          isChecked['timeZone'].value &&
          (isChecked['location'].value.length || isChecked['isSelectedAll'].value || isChecked['preSelectedLocation']?.value?.length)
            ? false
            : true);
      }
    } else {
      false;
    }
  }

}
